import React, { Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet, redirect, useLocation } from 'react-router-dom'

import SignUp from 'views/auth/views/signup'
import SignIn from 'views/auth/views/signin'
import SendForgotPasswordLink from 'views/auth/views/forgotpassword/SendForgotPasswordLink'
import SendEmailVerificationCode from 'views/auth/views/verifyemail/SendEmailVerificationCode'
import Home from 'views/home'
import Profile from 'views/profile'
import Hub from 'views/hub'
import Settings from 'views/settings'

// React Flow and dagre load only when the page is opened.
const Dependencies = React.lazy(() => import('views/dependencies'))
import Document from 'views/document'
import Table from 'views/table'
import AppSidebar from 'components/sidebar'
import NoPermission from 'views/auth/views/nopermission'
import Integrations from 'views/integrations'
import Security from 'views/security'
import MaintenancePage from 'views/maintenance'
import Audit from 'components/audit'
import Stylebook from 'views/stylebook'
import Organisation from 'views/organisation'
import OrganisationApiKeys from 'views/organisation/views/keys'
import { PROJECT as CONSTANT_PROJECT, MAINTENANCE_MODE } from 'app-constants'

export const BASE = '/'
export const HOME = '/home'
export const SIGN_UP = '/signup'
export const SIGN_IN = '/signin'
export const NO_PERMISSION = '/nopermission'
export const VERIFY_EMAIL = '/verify-email'
export const CONFIRM_EMAIL = '/confirm-email'
export const FORGOT_PASSWORD = '/forgot-password'
export const RESET_PASSWORD = '/reset-password'
export const APPLICATION_HOME = '/home'
export const PROFILE = '/profile/:userId/:tab'
export const INTEGRATIONS = '/settings/integrations'
export const SECURITY = '/settings/security'
export const HUB = '/hub/:projectId'
export const HUB_SETTINGS = '/hub/:projectId/settings'
export const HUB_DEPENDENCIES = '/hub/:projectId/relationships'
export const PROJECT = '/project/:projectId'
export const TABLE_BASE = '/table/'
export const TABLE_VIEW_BASE = '/view/'
export const OLD_TABLE = '/project/:projectId/table/:tableId/view/:viewId'
export const TABLE = '/hub/:projectId/table/:tableId/view/:viewId'
export const DOCUMENT = '/hub/:projectId/document/:processId'
export const PROCESS = '/project/:projectId/process/:processId'
export const DOCUMENT_BASE = '/document/'
export const HUB_BASE = '/hub/'
export const ORGANISATION_BASE = '/organisation/'
export const ORGANISATION = '/organisation/:organisationId'
export const ORGANISATION_DASHBOARD = '/organisation/:organisationId/dashboard'
export const ORGANISATION_API_KEYS = '/organisation/:organisationId/api-keys'
export const SETTINGS = '/hub/:projectId/settings'
export const TERMS = 'https://morta.io/terms'
export const PRIVACY = 'https://morta.io/privacy'
export const TWO_FACTOR_SIGNIN = '/signin/2fa'
export const ADMIN_HOME = '/admin/home'
export const ADMIN_CELERY = '/admin/celery'
export const ADMIN_LOGS = '/admin/logs'
export const ADMIN_REDIS = '/admin/redis'
export const ADMIN_ORGANISATIONS = '/admin/organisations'
export const ADMIN_LOCKS = '/admin/locks'
export const ADMIN_CONVERSATIONS = '/admin/conversations'
export const ADMIN_CONVERSATION_DETAIL = '/admin/conversations/:conversationId'
/** Dev-only component gallery. Not linked from the product UI. */
export const STYLEBOOK = '/stylebook'
export const TEAM_DASHBOARD = '/team/:teamId'
export const TEAM_SEATS = '/team/:teamId/seats'
export const ORGANISATION_TEAMS = '/organisation/:organisationId/teams'

import AuthGuard from 'components/guard/AuthGuard'
import ConfirmEmailVerificationCode from 'views/auth/views/verifyemail/ConfirmEmailVerificationCode'
import SetNewPassword from 'views/auth/views/forgotpassword/SetNewPassword'
import GoogleComplete from 'views/auth/views/oauth/GoogleComplete'
import MicrosoftComplete from 'views/auth/views/oauth/MicrosoftComplete'
import SAMLComplete from 'views/auth/views/saml/SAMLComplete'
import MasqueradeWarning from 'views/auth/components/MasqueradeWarning'
import FrontChannelLogout from 'views/auth/views/oauth/Logout'
import TwoFactorLogin from 'views/auth/views/2fa'
import AdminHome from 'views/admin/components/home'
import AdminCelery from 'views/admin/components/celery'
import AdminRedis from 'views/admin/components/redis'
import AdminOrganisations from 'views/admin/components/organisations'
import AdminLocks from 'views/admin/components/locks'
import AdminConversations from 'views/admin/components/conversations'
import AdminConversationDetail from 'views/admin/components/conversations/detail'
import TeamDashboard from 'views/team'
import TeamSeats from 'views/team/seats'
import TeamsOverview from 'views/organisation/components/teams'
import { useProject } from 'hooks/useProject'
import ArchivedWarning from 'components/archived'
import FreeTrial from 'components/trial'
import MortaSnackbar from 'components/snackbar'
import SessionTimeoutModal from 'components/session'
import { AppProviders } from 'providers'
import MortaAgent from 'components/agent'

// Oauth
export const GOOGLE_OAUTH_COMPLETE = '/oauth/google/complete'
export const MICROSOFT_OAUTH_COMPLETE = '/oauth/microsoft/complete'
export const SAML_COMPLETE = '/auth/saml/acs'
export const FRONT_CHANNEL_LOGOUT = '/oauth/logout'

// Root layout with providers and global components
const RootLayout: React.FC = () => {
  if (MAINTENANCE_MODE) {
    return <MaintenancePage />
  }

  return (
    <AppProviders>
      <MortaSnackbar />
      <SessionTimeoutModal />
      <div className="flex bg-surface text-body h-screen w-full">
        <Outlet />
      </div>
    </AppProviders>
  )
}

// Layout for authenticated routes with sidebar
const AuthenticatedLayout: React.FC = () => {
  const { project } = useProject()
  const location = useLocation()
  const hasContextRoute =
    location.pathname.includes('/view/') ||
    location.pathname.includes('/document/') ||
    location.pathname.includes('/process/')
  return (
    <AuthGuard>
      <div className="inline-flex w-[inherit]">
        <AppSidebar />
        {!hasContextRoute && <MortaAgent />}
        <MasqueradeWarning />

        {project.isDeleted && (
          <ArchivedWarning message={`This ${CONSTANT_PROJECT} has been archived by the ${CONSTANT_PROJECT} owner`} />
        )}

        <div className="flex flex-row flex-wrap w-full h-full bg-surface overflow-auto">
          <Outlet />
          <FreeTrial />
        </div>
      </div>
    </AuthGuard>
  )
}

// Helper to build redirect URL with search params preserved
const buildRedirectUrl = (request: Request, newPath: string) => {
  const url = new URL(request.url)
  return newPath + url.search
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public routes (no auth required)
      { path: SIGN_UP, element: <SignUp /> },
      { path: SIGN_IN, element: <SignIn /> },
      { path: TWO_FACTOR_SIGNIN, element: <TwoFactorLogin /> },
      { path: FORGOT_PASSWORD, element: <SendForgotPasswordLink /> },
      { path: RESET_PASSWORD, element: <SetNewPassword /> },
      { path: VERIFY_EMAIL, element: <SendEmailVerificationCode /> },
      { path: CONFIRM_EMAIL, element: <ConfirmEmailVerificationCode /> },

      // OAuth
      { path: GOOGLE_OAUTH_COMPLETE, element: <GoogleComplete /> },
      { path: MICROSOFT_OAUTH_COMPLETE, element: <MicrosoftComplete /> },
      { path: SAML_COMPLETE, element: <SAMLComplete /> },

      // No permission page (accessible without auth)
      { path: NO_PERMISSION, element: <NoPermission /> },

      // Dev-only component gallery. vite.config.ts defines NODE_ENV as a literal
      // at build time, so production builds drop this route (and the
      // then-unused Stylebook module) entirely.
      ...(import.meta.env.NODE_ENV !== 'production' ? [{ path: STYLEBOOK, element: <Stylebook /> }] : []),

      // Authenticated routes
      {
        element: <AuthenticatedLayout />,
        children: [
          // Legacy redirects
          {
            path: '/project/:projectId',
            loader: ({ params, request }) => redirect(buildRedirectUrl(request, `/hub/${params.projectId}`))
          },
          {
            path: '/project/:projectId/process/:processId',
            loader: ({ params, request }) =>
              redirect(buildRedirectUrl(request, `/hub/${params.projectId}/document/${params.processId}`))
          },
          {
            path: '/project/:projectId/table/:tableId/view/:viewId',
            loader: ({ params, request }) =>
              redirect(
                buildRedirectUrl(request, `/hub/${params.projectId}/table/${params.tableId}/view/${params.viewId}`)
              )
          },
          {
            path: '/project/:projectId/settings',
            loader: ({ params, request }) => redirect(buildRedirectUrl(request, `/hub/${params.projectId}/settings`))
          },

          // Main routes
          { path: APPLICATION_HOME, element: <Home /> },
          { path: HUB, element: <Hub /> },
          { path: HOME, element: <Home /> },
          { path: ORGANISATION_DASHBOARD, element: <Organisation /> },
          { path: ORGANISATION_API_KEYS, element: <OrganisationApiKeys /> },
          { path: DOCUMENT, element: <Document /> },
          { path: PROFILE, element: <Profile /> },
          { path: SETTINGS, element: <Settings /> },
          {
            path: HUB_DEPENDENCIES,
            element: (
              <Suspense fallback={null}>
                <Dependencies />
              </Suspense>
            )
          },
          { path: TABLE, element: <Table /> },
          { path: SECURITY, element: <Security /> },
          { path: INTEGRATIONS, element: <Integrations /> },

          // Team plan routes
          { path: ORGANISATION_TEAMS, element: <TeamsOverview /> },
          { path: TEAM_DASHBOARD, element: <TeamDashboard /> },
          { path: TEAM_SEATS, element: <TeamSeats /> },

          // Admin routes
          { path: ADMIN_HOME, element: <AdminHome /> },
          { path: ADMIN_CELERY, element: <AdminCelery /> },
          { path: ADMIN_REDIS, element: <AdminRedis /> },
          { path: ADMIN_LOGS, element: <Audit includeChange={false} className="p-5" /> },
          { path: ADMIN_ORGANISATIONS, element: <AdminOrganisations /> },
          { path: ADMIN_LOCKS, element: <AdminLocks /> },
          { path: ADMIN_CONVERSATIONS, element: <AdminConversations /> },
          { path: ADMIN_CONVERSATION_DETAIL, element: <AdminConversationDetail /> },

          // Front channel logout
          { path: FRONT_CHANNEL_LOGOUT, element: <FrontChannelLogout /> },

          // Catch-all redirect to home for authenticated users
          { path: '*', element: <Navigate to={HOME} replace /> }
        ]
      }
    ]
  }
])
